import type { Inventory, InventoryLevel } from "@/api/inventory/_models/Inventory";
import { INVENTORY_LIST_SYSTEM_LABEL, toCin7Level, toOnHandDifference, toThreePlLevelList } from "@/features/inventory/inventory-list/inventoryList.utils";

export type InventoryListSummaryTone = "success" | "warning" | "error" | "info";

export interface InventoryListSummary {
  tone: InventoryListSummaryTone;
  text: string;
}

const toSystemLabel = (system: string): string => INVENTORY_LIST_SYSTEM_LABEL[system] ?? system;

const toThreePlNames = (levels: InventoryLevel[]): string => {
  return levels.map((level) => toSystemLabel(level.system)).join(" and ");
};

const toCount = (value: number): string => Math.abs(value).toLocaleString();

/**
 * The sentence for an opened inventory row: what its status (the backend's
 * calculateInventoryStatus) means for it, in numbers, and its tone. null when
 * the row has no status yet.
 */
export const toInventoryListSummary = (row: Inventory): InventoryListSummary | null => {
  const cin7OnHand = toCin7Level(row)?.onHand ?? 0;
  const threePl = toThreePlLevelList(row);
  const threePlNames = toThreePlNames(threePl);
  const threePlOnHand = threePl.reduce((sum, level) => sum + (level.onHand ?? 0), 0);
  const difference = toOnHandDifference(row) ?? 0;

  if (row.status === "match") {
    return { tone: "success", text: `Cin7 and ${threePlNames || "the 3PL"} agree on stock.` };
  }
  if (row.status === "differs") {
    let direction = "fewer";
    if (difference > 0) direction = "more";
    return { tone: "warning", text: `${threePlNames || "The 3PL"} has ${toCount(difference)} ${direction} on hand than Cin7.` };
  }
  if (row.status === "missingInCin7") {
    return { tone: "error", text: `${threePlNames || "The 3PL"} holds ${toCount(threePlOnHand)} on hand but Cin7 shows none.` };
  }
  if (row.status === "missingAt3pl") {
    return { tone: "error", text: `Cin7 holds ${toCount(cin7OnHand)} on hand but the 3PL hasn't reported this SKU.` };
  }
  if (row.status === "cin7Only") {
    if (cin7OnHand === 0) return { tone: "success", text: `No stock in Cin7 and no 3PL holds ${row.locationName}.` };
    return { tone: "info", text: `No 3PL holds ${row.locationName}, so only Cin7 stock is tracked.` };
  }
  if (row.status === "threePlOnly") {
    return { tone: "info", text: `No Cin7 location covers ${row.locationName}, so only ${threePlNames || "the 3PL"}'s stock is shown.` };
  }
  return null;
};
